<!--
  A page to view and edit a user
-->

<template>
  <div v-loading="loading">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item>
        <router-link :to="{ name: 'home' }">
          Home
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item>
        <router-link :to="{ name: 'users' }">
          Users
        </router-link>
      </el-breadcrumb-item>
      <el-breadcrumb-item>{{userId==='new'?'Create user':'User details'}}</el-breadcrumb-item>
    </el-breadcrumb>
    <main-title>User {{data.email}} ({{data.role}})</main-title>
    <el-button-group style="justify-content: center;">
      <el-button @click="remove" type="danger">Delete</el-button>
    </el-button-group>
    <el-form :model="password" ref="passwordForm"
      label-width="160px"
      :hide-required-asterisk="true">
      <subtitle>Login</subtitle>
      <!-- email cannot be changed -->
      <el-form-item label="Email">
        <el-input v-if="userId==='new'"
          type="email" :value="password.email"></el-input>
        <el-input v-else disabled
          type="email" :value="data.email"></el-input>
      </el-form-item>
      <!-- password edition -->
      <template v-if="userId==='new' || !userId">
        <el-form-item label="Password" prop="new_password" required>
          <el-input type="password" v-model="password.new_password"></el-input>
        </el-form-item>
        <el-form-item label="Password confirmation" prop="confirm_password"
          required :rules="confirmValidator">
          <el-input type="password" v-model="password.confirm_password"></el-input>
        </el-form-item>
        <el-button-group v-if="!userId" style="justify-content: center;">
          <el-button @click="changePassword" type="success">Change password</el-button>
        </el-button-group>
        <el-button-group v-else style="justify-content: center;">
          <el-button @click="createUser" type="success">Create user</el-button>
        </el-button-group>
      </template>
    </el-form>
    <el-form :model="copy" ref="form"
      label-width="160px"
      :hide-required-asterisk="true">
      <subtitle>Authorizations</subtitle>
      <!-- role selector -->
      <el-form-item label="Role" prop="role">
        <el-select v-model="copy.role">
          <el-option value="client">Client</el-option>
          <el-option value="admin">Admin</el-option>
        </el-select>
      </el-form-item>
      <!-- admin authorizations editor -->
      <template v-if="copy.role === 'admin'">
        <el-form-item label="Public skills">
          <el-select v-model="copy.authorizations.public" required>
            <el-option v-for="level in rights" :key="level.value"
              :lable="level.name"
              :value="level.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Owned skills">
          <el-select v-model="copy.authorizations.owned" required>
            <el-option v-for="level in rights" :key="level.value"
              :lable="level.name"
              :value="level.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Global entities">
          <el-select v-model="copy.authorizations.global" required>
            <el-option v-for="level in rights" :key="level.value"
              :lable="level.name"
              :value="level.value"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Users">
          <el-select v-model="copy.authorizations.user" required>
            <el-option v-for="level in rights" :key="level.value"
              :lable="level.name"
              :value="level.value"></el-option>
          </el-select>
        </el-form-item>
      </template>
      <el-button-group style="justify-content: center;">
        <el-button @click="save" type="success">Save</el-button>
      </el-button-group>
    </el-form>
  </div>
</template>

<script>
import State from '../../stores/state.js'

// authorizations options
const RIGHTS = [
  { name: 'Forbidden', value: 'forbidden' },
  { name: 'Read', value: 'read' },
  { name: 'Edit', value: 'edit' },
  { name: 'Publish', value: 'publish' },
  { name: 'Admin', value: 'admin' },
]

export default {
  props: {
    user: String,
  },
  data () {
    // check that password and confirm-password are identical
    let confirmValidator = {
      validator: (rule, value, callback) => {
        if (this.password.new_password !== this.password.confirm_password) {
          callback(new Error('confirmation is different than password'))
        } else callback()
      },
    }
    return {
      loading: true,
      state: State.state,
      // "new" for a new user, then updated on creation (currently not used)
      // null for the current user
      userId: null,
      data: {},
      copy: {}, // a copy of data for saving
      password: { // for password edition or user creation
        email: '',
        new_password: '',
        confirm_password: '',
      },
      rights: RIGHTS,
      confirmValidator,
    }
  },
  compiled: {
    isMe () { return !!this.state.user && this.state.user.user === this.user },
  },
  inject: ['onerror'],
  created () { this.loadData() },
  watch: {
    'state.user' () { this.loadData() },
    'user' () { this.loadData() },
    'data' (data) {
      this.copy = {
        role: data.role,
        authorizations: { ...data.authorizations },
      }
      this.password = {
        email: '',
        new_password: '',
        confirm_password: '',
      }
    },
    'copy.role' (role) {
      if (role === this.data.role) {
        this.copy.authorizations = { ...this.data.authorizations }
      } else if (role === 'admin') {
        this.copy.authorizations = {
          public: 'forbidden',
          owned: 'forbidden',
          global: 'forbidden',
          user: 'forbidden',
        }
      } else this.copy.authorizations = {}
    },
    isMe (val) {
      if (val) this.$router.replace({ name: 'user', user: 'me' })
    },
  },
  methods: {
    // load the user data
    loadData () {
      if (this.user === 'me') this.userId = null
      else this.userId = this.user
      if (this.userId === 'new') {
        this.loading = false
        this.data = { email: 'New user', role: 'admin' }
      } else if (this.userId) {
        this.loading = true
        State.getUser(this.userId).then(data => {
          this.data = data
        }, this.onerror).finally(() => { this.loading = false })
      } else {
        if (this.state.user) {
          this.loading = false
          this.data = this.state.user
        } else {
          this.loading = true
        }
      }
    },
    // save the user
    save () {
      this.$refs.form.validate((valid, errors) => {
        if (valid) {
          let data = {
            role: this.copy.role,
            authorizations: { ...this.copy.authorizations },
          }
          return State.saveUser(this.userId, data).then(data => {
            this.$message({
              message: 'User saved',
              type: 'success',
            })
            this.data = data
          }, this.onerror).finally(() => { this.loading = false })
        } else {
          console.error(errors)
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
    // create a new admin user
    createUser () {
      // TODO
    },
    // change own password
    changePassword () {
      this.$refs.form.validate((valid, errors) => {
        if (valid) {
          let data = { new_password: this.password.new_password }
          this.$prompt('Please confirm your password',
            'Password confirmation', {
              inputType: 'password',
            }).then(password => {
            data.current_password = password.value
            return State.saveUser(this.userId, data).then(data => {
              this.$message({
                message: 'User saved',
                type: 'success',
              })
              this.data = data
            }, this.onerror).finally(() => { this.loading = false })
          })
        } else {
          console.error(errors)
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
    // delete the user
    remove () {
      this.$confirm('This will permanently delete the user', 'Delete', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }).then(() => {
        State.removeUser(this.userId).then(() => {
          this.$router.replace('/users')
        }, this.onerror)
      })
    },
  },
}
</script>
